'use strict';

/**
 * SuperAgencySettingsController
 *
 * Super-admin "Agency Settings" (AMS) page (WEB-3553). A super user selects an agency and
 * manages per-agency configuration. Both flags are read from the `sec/super/agencies` list
 * payload (iteration 2 — `sec/agencies` does not exist; the agencies listing route lives in
 * the PUBLIC group only), so selecting an agency needs no extra request.
 *
 * Sections:
 *   1. Special Password (gated by SS_SET_AGENCY_SPECIAL_PASSWORD) — enable/disable the agency's
 *      Team-IC special password and set/change its value. Per-section view/edit mode like the
 *      Security section: the switch is read-only until Edit; Save opens the set-password modal
 *      when enabling, asks for confirmation when disabling, and is a no-op when nothing changed.
 *      "Change Password" (edit mode, while the saved state is enabled) reopens the modal.
 *      Persisted via POST sec/agencies/{id}/set-special-password {enabled, password?}.
 *      Consolidates the former standalone WEB-3301 Super Agencies page.
 *   2. Security (gated by SS_ORG_2FA_ENFORCEMENT) — "Enforce 2FA at login for all users"
 *      (`is_twofa_enforced` on the agency). Per-section view/edit mode like the Developer
 *      Settings page. Persisted via POST sec/super/agencies/{id}/set-2fa-enforcement {enabled}.
 *
 * The agency dropdown is a searchable jQuery Nice Select widget (class "dashboard-select",
 * initialised in the blade); it is rebuilt via niceSelect('update') once the async agency
 * list resolves so the late-populated ng-options render.
 */
class SuperAgencySettingsController {
    /**
     * @param {Object}   SSUtilService
     * @param {Object}   SSUserService         - permission/role helper (isSuper, hasPermission)
     * @param {Object}   SSHttpService         - API request helper
     * @param {Object}   OTPService            - 2FA gate for sensitive saves
     * @param {Object}   SSAlertService        - user-facing alerts
     * @param {Object}   SSConfirmationService - yes/no confirmation modal
     * @param {Function} $timeout              - Angular $timeout (defer nice-select refresh past digest)
     */
    constructor(SSUtilService, SSUserService, SSHttpService, OTPService, SSAlertService, SSConfirmationService, $timeout) {
        const self = this;

        self.SSUtilService = SSUtilService;
        self.SSUserService = SSUserService;
        self.SSHttpService = SSHttpService;
        self.OTPService = OTPService;
        self.SSAlertService = SSAlertService;
        self.SSConfirmationService = SSConfirmationService;
        self.$timeout = $timeout;

        self.data = SHOWSUITE.data;

        self.agencies = null;
        self.selectedAgency = null;     // selected agency id (ng-model of the dropdown)
        self.selectedAgencyObj = null;  // selected agency row from the list payload

        // Section 1 — Special Password (WEB-3301, consolidated here)
        self.specialPasswordEnabled = false;        // toggle model, mirrors agency.is_special_password_enabled
        self.specialPasswordEditMode = false;       // false = read-only, true = editing
        self._specialPasswordSnapshot = false;      // value captured on entering edit (for Cancel)
        self.new_password = '';
        self.confirm_password = '';
        self.set_password_error = '';
        self.saving = false;

        // Section 2 — Security (WEB-3553): org-level enforced 2FA at login
        self.twofaEnforced = false;       // toggle model, mirrors agency.is_twofa_enforced
        self.securityEditMode = false;    // false = read-only, true = editing
        self._securitySnapshot = false;   // value captured on entering edit (for Cancel)

        if (self.SSUserService.isSuper()) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.getAgencies();
    }

    /**
     * Whether the current user may manage the agency's special password (section 1).
     * @returns {boolean}
     */
    canManageSpecialPassword() {
        return this.SSUserService.hasPermission('SS_SET_AGENCY_SPECIAL_PASSWORD');
    }

    /**
     * Whether the current user may manage org-level 2FA enforcement (section 2).
     * @returns {boolean}
     */
    canManageOrg2FA() {
        return this.SSUserService.hasPermission('SS_ORG_2FA_ENFORCEMENT');
    }

    /**
     * Load the agency dropdown options from the super list endpoint (returns id, name and
     * both security flags, ordered by name), then rebuild the searchable nice-select widget.
     * The endpoint requires SS_ORG_2FA_ENFORCEMENT (same gate as the other org-security
     * endpoints) — a permission error surfaces via the standard alert.
     */
    getAgencies() {
        const self = this;

        loading(true);
        self.SSHttpService.getAPIRequest('sec/super/agencies').then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            self.agencies = (response.data.agencies || []).map(function (agency) {
                agency.is_special_password_enabled = parseInt(agency.is_special_password_enabled || 0, 10);
                agency.is_twofa_enforced = parseInt(agency.is_twofa_enforced || 0, 10);
                return agency;
            });
            // ng-options populate after this digest; rebuild the nice-select so the options render
            self.$timeout(function () {
                jQuery('#agency-select').niceSelect('update');
            });
        });
    }

    /**
     * ng-change handler for the agency dropdown. Resets every section to read-only and hydrates
     * both sections' models from the already-loaded agencies list (no extra request needed).
     */
    agencySelected() {
        const self = this;

        self.specialPasswordEditMode = false;
        self.securityEditMode = false;
        self.selectedAgencyObj = (self.agencies || []).find(function (a) {
            return a.id == self.selectedAgency;
        }) || null;

        self.specialPasswordEnabled = !!(self.selectedAgencyObj && self.selectedAgencyObj.is_special_password_enabled);
        self.twofaEnforced = !!(self.selectedAgencyObj && self.selectedAgencyObj.is_twofa_enforced);
    }

    // ---- Section 1: Special Password ----

    /** Enter edit mode for the Special Password section (snapshots current value for Cancel). */
    enableSpecialPasswordEdit() {
        const self = this;
        if (!self.selectedAgencyObj || !self.canManageSpecialPassword()) {
            return;
        }
        self._specialPasswordSnapshot = self.specialPasswordEnabled;
        self.specialPasswordEditMode = true;
    }

    /** Discard the unsaved Special Password change and return to read-only. */
    cancelSpecialPasswordEdit() {
        const self = this;
        self.specialPasswordEnabled = self._specialPasswordSnapshot;
        self.specialPasswordEditMode = false;
    }

    /**
     * Persist the Special Password section. Enabling needs a password, so Save opens the
     * set-password modal (the POST happens on the modal's Save); disabling asks for
     * confirmation first. When the toggle was not changed this just leaves edit mode.
     * A cancelled modal/confirmation keeps the section in edit mode with the unsaved value.
     */
    saveSpecialPasswordSettings() {
        const self = this;
        if (!self.selectedAgencyObj || !self.canManageSpecialPassword() || !self.specialPasswordEditMode) {
            return;
        }

        if (self.specialPasswordEnabled === self._specialPasswordSnapshot) {
            self.specialPasswordEditMode = false;
            return;
        }

        if (self.specialPasswordEnabled) {
            self.openSetPasswordModal();
        } else {
            self.SSConfirmationService.getConfirmed(
                'Disable Special Password',
                'Disable the special password for "' + self.selectedAgencyObj.name + '"? ' +
                'Team-IC users will no longer be prompted for it before sensitive actions.',
                self.SSConfirmationService.TYPE_YES_NO,
                function (option) {
                    if (option !== self.SSConfirmationService.OPTION_YES) {
                        return;
                    }
                    self._setSpecialPassword({enabled: false});
                }
            );
        }
    }

    /** Open the set/change password modal with a clean state (edit mode only). */
    openSetPasswordModal() {
        const self = this;
        if (!self.selectedAgencyObj || !self.canManageSpecialPassword() || !self.specialPasswordEditMode) {
            return;
        }
        self.new_password = '';
        self.confirm_password = '';
        self.set_password_error = '';
        $('#setAgencyPasswordModal').modal('show');
    }

    /** Validate the modal inputs and persist the (new) special password. */
    submitSetPassword() {
        const self = this;
        self.set_password_error = '';

        if (!self.selectedAgencyObj) {
            return;
        }
        if (!self.new_password || self.new_password.length < 6) {
            self.set_password_error = 'Password must be at least 6 characters.';
            return;
        }
        if (self.new_password !== self.confirm_password) {
            self.set_password_error = 'Passwords do not match.';
            return;
        }

        self._setSpecialPassword({enabled: true, password: self.new_password}, function () {
            $('#setAgencyPasswordModal').modal('hide');
        });
    }

    /**
     * POST the special-password change for the selected agency, then sync the local models
     * (section toggle + cached agencies list row) from the server-confirmed value and return
     * the section to read-only.
     *
     * @param {Object}   payload   - {enabled: boolean, password?: string}
     * @param {Function} [onSuccess] - invoked after a successful save (e.g. close the modal)
     */
    _setSpecialPassword(payload, onSuccess) {
        const self = this;
        let url = 'sec/agencies/' + self.selectedAgency + '/set-special-password';

        self.saving = true;
        loading(true);
        self.SSHttpService.postAPIRequest(url, payload).then(function (response) {
            loading(false);
            self.saving = false;

            if (response instanceof Error) {
                self.set_password_error = 'Could not update the special password.';
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let enabled = (response.data && typeof response.data.is_special_password_enabled !== 'undefined')
                ? !!parseInt(response.data.is_special_password_enabled, 10)
                : !!payload.enabled;
            self.specialPasswordEnabled = enabled;
            if (self.selectedAgencyObj) {
                self.selectedAgencyObj.is_special_password_enabled = enabled ? 1 : 0;
            }

            // don't keep the plain password in the model once it has been persisted
            self.new_password = '';
            self.confirm_password = '';

            self.specialPasswordEditMode = false;
            self.SSAlertService.success(payload.enabled ? 'Special password has been set successfully.' : 'Special password has been disabled.');

            if (onSuccess) {
                onSuccess();
            }
        });
    }

    // ---- Section 2: Security (WEB-3553) ----

    /** Enter edit mode for the Security section (snapshots current value for Cancel). */
    enableSecurityEdit() {
        const self = this;
        if (!self.canManageOrg2FA()) {
            return;
        }
        self._securitySnapshot = self.twofaEnforced;
        self.securityEditMode = true;
    }

    /** Discard the unsaved Security change and return to read-only. */
    cancelSecurityEdit() {
        const self = this;
        self.twofaEnforced = self._securitySnapshot;
        self.securityEditMode = false;
    }

    /** Persist the Security section. Gated by SS_ORG_2FA_ENFORCEMENT + 2FA. */
    saveSecuritySettings() {
        const self = this;
        if (!self.selectedAgency || !self.canManageOrg2FA() || !self.securityEditMode) {
            return;
        }
        self.OTPService.getVerified(self.OTPService.TYPE_SESSION_2FA, function () {
            self._saveSecuritySettings();
        });
    }

    /**
     * POST the enforcement flag for the selected agency, then sync the local models
     * (toggle + cached agencies list row) from the server-confirmed value.
     */
    _saveSecuritySettings() {
        const self = this;
        let url = 'sec/super/agencies/' + self.selectedAgency + '/set-2fa-enforcement';

        loading(true);
        self.SSHttpService.postAPIRequest(url, {enabled: !!self.twofaEnforced}).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let enforced = !!(response.data && parseInt(response.data.is_twofa_enforced || 0, 10));
            self.twofaEnforced = enforced;
            if (self.selectedAgencyObj) {
                self.selectedAgencyObj.is_twofa_enforced = enforced ? 1 : 0;
            }

            self.securityEditMode = false;
            self.SSAlertService.success('Security settings have been saved successfully.');
        });
    }

}

SuperAgencySettingsController.$inject = ['SSUtilService', 'SSUserService', 'SSHttpService', 'OTPService', 'SSAlertService', 'SSConfirmationService', '$timeout'];
app.controller('SuperAgencySettingsController', SuperAgencySettingsController);
